import { useCallback, useEffect, useState } from 'react';
import { api, fetchAutenticado, ErrorApi } from '../api/cliente';
import type {
  Compatibilidad, Opcion, Pagina, Refaccion,
} from '../api/tipos';
import { useSesion } from '../sesion';
import {
  Cargando, Dialogo, DialogoMotivo, Error, Insignia, Paginacion,
} from '../componentes/comunes';

interface Filtros {
  busqueda: string;
  areaId: string;
  tipoEquipoId: string;
  marcaId: string;
  modeloId: string;
  estado: string;
}

const FILTROS_VACIOS: Filtros = {
  busqueda: '', areaId: '', tipoEquipoId: '', marcaId: '', modeloId: '', estado: '',
};

/**
 * Refacciones y su matriz de compatibilidad.
 *
 * Los filtros de tipo de equipo, marca y modelo no consultan la refacción
 * sino la matriz: responden a "qué piezas sirven para esta máquina", que
 * es la pregunta con la que llega el almacén.
 */
export default function Refacciones() {
  const { puede } = useSesion();

  const [filtros, setFiltros] = useState<Filtros>(FILTROS_VACIOS);
  const [numero, setNumero] = useState(1);
  const [pagina, setPagina] = useState<Pagina<Refaccion> | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const [areas, setAreas] = useState<Opcion[]>([]);
  const [tiposEquipo, setTiposEquipo] = useState<Opcion[]>([]);
  const [marcas, setMarcas] = useState<Opcion[]>([]);
  const [modelos, setModelos] = useState<Opcion[]>([]);

  const [editando, setEditando] = useState<Refaccion | null>(null);
  const [creando, setCreando] = useState(false);
  const [subiendoCsv, setSubiendoCsv] = useState(false);
  const [viendo, setViendo] = useState<Refaccion | null>(null);
  const [cambiandoEstado, setCambiandoEstado] = useState<Refaccion | null>(null);

  useEffect(() => {
    api.get<Opcion[]>('/catalogos/areas/opciones').then(setAreas).catch(() => undefined);
    api.get<Opcion[]>('/catalogos/tipos-equipo/opciones').then(setTiposEquipo).catch(() => undefined);
    api.get<Opcion[]>('/catalogos/marcas/opciones').then(setMarcas).catch(() => undefined);
  }, []);

  /**
   * Los modelos, acotados a la marca **y al tipo de equipo**.
   *
   * Faltaba el tipo, y de ahí salía el 500 al guardar: se podía elegir
   * «cámara de refrigeración» con un modelo de la marca que en realidad
   * es de un horno, y el disparador de la 0002 lo rechazaba en SQL. La
   * pantalla decía «Ocurrió un error inesperado».
   *
   * Por marca y no todos: son más de cuatrocientos.
   */
  useEffect(() => {
    if (!filtros.marcaId) { setModelos([]); return; }
    api.get<Opcion[]>('/catalogos/modelos/opciones', {
      marca_id: filtros.marcaId,
      tipo_equipo_id: filtros.tipoEquipoId || undefined,
    })
      .then(setModelos)
      .catch(() => setModelos([]));
  }, [filtros.marcaId, filtros.tipoEquipoId]);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<Pagina<Refaccion>>('/refacciones', {
      busqueda: filtros.busqueda || undefined,
      areaId: filtros.areaId || undefined,
      tipoEquipoId: filtros.tipoEquipoId || undefined,
      marcaId: filtros.marcaId || undefined,
      modeloId: filtros.modeloId || undefined,
      estado: filtros.estado || undefined,
      pagina: numero,
      limite: 25,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [filtros, numero]);

  useEffect(() => {
    const t = setTimeout(cargar, filtros.busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, filtros.busqueda]);

  function cambiar(parcial: Partial<Filtros>) {
    setFiltros((f) => ({ ...f, ...parcial }));
    setNumero(1);
  }

  const hayFiltros = Object.values(filtros).some(Boolean);

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Refacciones</h1>
          <p className="sub">
            Catálogo de piezas y los equipos donde sirve cada una.
          </p>
        </div>
        {puede('refacciones', 'crear') && (
          <div className="controles">
            <button onClick={() => setSubiendoCsv(true)}>Cargar CSV</button>
            <button className="primario" onClick={() => setCreando(true)}>Nueva refacción</button>
          </div>
        )}
      </div>

      <div className="panel">
        <div className="controles">
          <input
            type="search"
            placeholder="Código, número de parte o nombre…"
            value={filtros.busqueda}
            onChange={(e) => cambiar({ busqueda: e.target.value })}
          />
          <select value={filtros.areaId} onChange={(e) => cambiar({ areaId: e.target.value })}>
            <option value="">Todas las áreas</option>
            {areas.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
          </select>
          <select
            value={filtros.tipoEquipoId}
            /* Cambiar el tipo limpia el modelo: el que estaba elegido
               puede ser de otro tipo, y el que se queda pegado es el que
               despues no deja guardar. La marca ya lo hacia. */
            onChange={(e) => cambiar({ tipoEquipoId: e.target.value, modeloId: '' })}
          >
            <option value="">Todos los equipos</option>
            {tiposEquipo.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select>
          <select
            value={filtros.marcaId}
            onChange={(e) => cambiar({ marcaId: e.target.value, modeloId: '' })}
          >
            <option value="">Todas las marcas</option>
            {marcas.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
          </select>
          <select
            value={filtros.modeloId}
            onChange={(e) => cambiar({ modeloId: e.target.value })}
            disabled={!filtros.marcaId}
          >
            <option value="">{filtros.marcaId ? 'Todos los modelos' : 'Elige una marca'}</option>
            {modelos.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
          </select>
          <select value={filtros.estado} onChange={(e) => cambiar({ estado: e.target.value })}>
            <option value="">Todos</option>
            <option value="activo">Sólo activas</option>
            <option value="inactivo">Sólo inactivas</option>
          </select>
          {hayFiltros && (
            <button onClick={() => { setFiltros(FILTROS_VACIOS); setNumero(1); }}>
              Limpiar
            </button>
          )}
        </div>

        <Error error={error} />

        {cargando && !pagina ? <Cargando /> : pagina && (
          <>
            {pagina.data.length === 0 ? (
              <div className="vacio">No hay refacciones que cumplan esos filtros.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Nº de parte</th>
                    <th>Nombre</th>
                    <th>Área</th>
                    <th className="numero">Mínimo</th>
                    <th className="numero">Costo</th>
                    <th className="numero">Equipos</th>
                    <th>Estado</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {pagina.data.map((r) => (
                    <tr key={r.id}>
                      <td className="mono">{r.codigo}</td>
                      <td className="mono">{r.numeroParte ?? '—'}</td>
                      <td>{r.nombre}</td>
                      <td>{r.area?.nombre ?? '—'}</td>
                      <td className="numero">{r.stockMinimo}</td>
                      <td className="numero">{moneda(r.costoPromedio)}</td>
                      <td className="numero">
                        <button className="enlace" onClick={() => setViendo(r)}>
                          {r.compatibilidades ?? 0}
                        </button>
                      </td>
                      <td><Insignia activo={r.activo} /></td>
                      <td className="acciones">
                        {puede('refacciones', 'editar') && (
                          <>
                            <button className="enlace" onClick={() => setEditando(r)}>Editar</button>
                            <button className="enlace" onClick={() => setCambiandoEstado(r)}>
                              {r.activo ? 'Desactivar' : 'Reactivar'}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <Paginacion
              pagina={pagina.pagina}
              limite={pagina.limite}
              total={pagina.total}
              onCambiar={setNumero}
            />
          </>
        )}
      </div>

      {subiendoCsv && (
        <CargaDeRefacciones
          onCerrar={() => setSubiendoCsv(false)}
          onAplicada={() => { setSubiendoCsv(false); cargar(); }}
        />
      )}

      {(creando || editando) && (
        <FormularioRefaccion
          areas={areas}
          tiposEquipo={tiposEquipo}
          marcas={marcas}
          refaccion={editando}
          onCerrar={() => { setCreando(false); setEditando(null); }}
          onGuardado={() => { setCreando(false); setEditando(null); cargar(); }}
        />
      )}

      {viendo && (
        <Compatibilidades
          refaccion={viendo}
          tiposEquipo={tiposEquipo}
          marcas={marcas}
          puedeEditar={puede('refacciones', 'editar')}
          onCerrar={() => { setViendo(null); cargar(); }}
        />
      )}

      {cambiandoEstado && (
        <CambioEstadoRefaccion
          refaccion={cambiandoEstado}
          onCerrar={() => setCambiandoEstado(null)}
          onHecho={() => { setCambiandoEstado(null); cargar(); }}
        />
      )}
    </>
  );
}

function moneda(valor: string | null): string {
  if (valor === null) return '—';
  return Number(valor).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}

// ══════════════════════════════════════════════════════════ formulario

/**
 * Sube el plano de la pieza.
 *
 * FormData y no JSON: el archivo viaja binario, sin inflarse un tercio
 * como haría en base64.
 */
async function subirDespiece(refaccionId: string, archivo: File): Promise<void> {
  const cuerpo = new FormData();
  cuerpo.append('archivo', archivo);

  const respuesta = await fetchAutenticado(`/api/v1/refacciones/${refaccionId}/despiece`, {
    method: 'POST',
    body: cuerpo,
  });

  if (!respuesta.ok) {
    const error = await respuesta.json().catch(() => ({}));
    throw new ErrorApi(
      respuesta.status,
      String(error.codigo_negocio ?? 'error'),
      String(error.detail ?? 'No se pudo subir el despiece.'),
    );
  }
}

/**
 * Elige la bodega donde queda dada de alta la pieza, y deja abrir una
 * nueva sin salir de aquí.
 *
 * Almacén tiene endpoint propio para esto: esperar a que alguien con
 * permiso de catálogos abra la bodega es lo que hace que la pieza se
 * capture "para después" y no se capture nunca.
 */
function SelectorDeBodega({
  valor, onCambiar,
}: {
  valor: string;
  onCambiar: (id: string) => void;
}) {
  const [bodegas, setBodegas] = useState<Opcion[]>([]);
  const [abriendo, setAbriendo] = useState(false);
  const [nombre, setNombre] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  const cargar = useCallback(() => {
    api.get<Opcion[]>('/almacen/bodegas')
      .then(setBodegas)
      .catch(() => setBodegas([]));
  }, []);

  useEffect(cargar, [cargar]);

  async function crear() {
    setError(null);
    setTrabajando(true);
    try {
      const creada = await api.post<{ id: string; nombre: string }>(
        '/almacen/bodegas', { nombre: nombre.trim() },
      );
      setBodegas((antes) => [...antes, { id: creada.id, nombre: creada.nombre }]);
      onCambiar(creada.id);
      setNombre('');
      setAbriendo(false);
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <div className="campo">
      <Error error={error} />

      <label htmlFor="bodegaId">Bodega</label>
      <select id="bodegaId" value={valor} onChange={(e) => onCambiar(e.target.value)}>
        <option value="">— no asignar todavía —</option>
        {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
      </select>

      {abriendo ? (
        <div className="controles" style={{ marginTop: 8 }}>
          <input
            value={nombre} maxLength={80} placeholder="Nombre de la bodega"
            onChange={(e) => setNombre(e.target.value)}
          />
          <button
            className="primario"
            onClick={() => void crear()}
            disabled={trabajando || nombre.trim().length < 2}
          >
            {trabajando ? 'Abriendo…' : 'Abrir bodega'}
          </button>
          <button onClick={() => { setAbriendo(false); setNombre(''); }}>Cancelar</button>
        </div>
      ) : (
        <button className="enlace" onClick={() => setAbriendo(true)}>
          Agregar nueva bodega
        </button>
      )}

      <span className="ayuda">
        La pieza queda con existencia en cero, para que aparezca en Almacén
        desde ahora y no hasta su primera entrada.
      </span>
    </div>
  );
}

/** El PDF del plano: lo que hay, y lo que se va a subir. */
function Despiece({
  refaccion, archivo, onElegir,
}: {
  refaccion: Refaccion | null;
  archivo: File | null;
  onElegir: (archivo: File | null) => void;
}) {
  const [motivo, setMotivo] = useState<string | null>(null);

  function elegir(elegido: File | null) {
    setMotivo(null);
    if (!elegido) { onElegir(null); return; }

    // Se avisa aquí de lo que la API va a rechazar de todas formas. El
    // viaje de subida y el error de vuelta no aportan nada que no se
    // pueda saber mirando el archivo.
    if (elegido.type && elegido.type !== 'application/pdf') {
      setMotivo('El despiece tiene que ser un PDF.');
      onElegir(null);
      return;
    }
    if (elegido.size > 2 * 1024 * 1024) {
      setMotivo(`Pesa ${(elegido.size / 1024 / 1024).toFixed(1)} MB y el máximo son 2 MB.`);
      onElegir(null);
      return;
    }
    onElegir(elegido);
  }

  return (
    <div className="campo">
      {refaccion?.despiece && !archivo && (
        <p>
          {/* No es un enlace: el token vive sólo en memoria y no viaja en
              una navegación, así que abrirlo así devolvía 401 y el plano
              parecía perdido. */}
          Ya tiene uno:{' '}
          <button
            className="enlace"
            onClick={() => {
              void api.descargar(
                `/refacciones/${refaccion.id}/despiece`,
                undefined,
                refaccion.despiece!.nombre,
              );
            }}
          >
            {refaccion.despiece.nombre}
          </button>
          . Subir otro lo reemplaza.
        </p>
      )}

      <input
        type="file"
        accept="application/pdf"
        onChange={(e) => elegir(e.target.files?.[0] ?? null)}
      />

      {motivo
        ? <span className="ayuda" style={{ color: '#b02a37' }}>{motivo}</span>
        : <span className="ayuda">PDF, hasta 2 MB. Un documento por pieza.</span>}
    </div>
  );
}

function FormularioRefaccion({
  areas, tiposEquipo, marcas, refaccion, onCerrar, onGuardado,
}: {
  areas: Opcion[];
  tiposEquipo: Opcion[];
  marcas: Opcion[];
  refaccion: Refaccion | null;
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const esAlta = refaccion === null;
  const [datos, setDatos] = useState({
    codigo: refaccion?.codigo ?? '',
    numeroParte: refaccion?.numeroParte ?? '',
    nombre: refaccion?.nombre ?? '',
    descripcion: refaccion?.descripcion ?? '',
    areaId: refaccion?.area?.id ?? '',
    unidadMedida: refaccion?.unidadMedida ?? 'pza',
    stockMinimo: String(refaccion?.stockMinimo ?? 0),
    costoPromedio: refaccion?.costoPromedio ?? '0',
    precioVentaSugerido: refaccion?.precioVentaSugerido ?? '',
  });

  // Dónde sirve la pieza. Se captura en el alta porque después nadie
  // vuelve, y una refacción sin compatibilidad no la encuentra quien la
  // necesita. Ninguno es obligatorio: la matriz que ya está cargada tiene
  // compatibilidades parciales y exigirlos trabaría la edición de todas.
  const [compat, setCompat] = useState({ tipoEquipoId: '', marcaId: '', modeloId: '' });
  const [modelos, setModelos] = useState<Opcion[]>([]);

  const [bodegaId, setBodegaId] = useState('');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  /**
   * Los modelos, acotados a la marca **y al tipo de equipo**.
   *
   * Faltaba el tipo, y de ahí salía el 500 al guardar: se podía elegir
   * «cámara de refrigeración» con un modelo de la marca que en realidad
   * es de un horno, y el disparador de la 0002 lo rechazaba en SQL. La
   * pantalla decía «Ocurrió un error inesperado».
   *
   * Por marca y no todos: son más de cuatrocientos.
   */
  useEffect(() => {
    if (!compat.marcaId) { setModelos([]); return; }
    api.get<Opcion[]>('/catalogos/modelos/opciones', {
      marca_id: compat.marcaId,
      tipo_equipo_id: compat.tipoEquipoId || undefined,
    })
      .then(setModelos)
      .catch(() => setModelos([]));
  }, [compat.marcaId, compat.tipoEquipoId]);

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      const cuerpo = {
        codigo: datos.codigo.trim(),
        numeroParte: datos.numeroParte.trim() || undefined,
        nombre: datos.nombre.trim(),
        descripcion: datos.descripcion.trim() || undefined,
        areaId: datos.areaId || undefined,
        unidadMedida: datos.unidadMedida.trim() || 'pza',
        stockMinimo: Number(datos.stockMinimo) || 0,
        costoPromedio: Number(datos.costoPromedio) || 0,
        precioVentaSugerido: datos.precioVentaSugerido
          ? Number(datos.precioVentaSugerido)
          : undefined,
      };

      let id = refaccion?.id;

      if (esAlta) {
        const creada = await api.post<{ id: string }>('/refacciones', {
          ...cuerpo,
          compatibilidad:
            compat.tipoEquipoId || compat.marcaId || compat.modeloId
              ? {
                  tipoEquipoId: compat.tipoEquipoId || undefined,
                  marcaId: compat.marcaId || undefined,
                  modeloId: compat.modeloId || undefined,
                }
              : undefined,
          bodegaId: bodegaId || undefined,
        });
        id = creada.id;
      } else {
        await api.patch(`/refacciones/${refaccion!.id}`, cuerpo);
      }

      // El despiece va después: la subida necesita el id, y en el alta ese
      // id no existe hasta que la refacción está guardada.
      if (archivo && id) await subirDespiece(id, archivo);

      onGuardado();
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={esAlta ? 'Nueva refacción' : `Editar ${refaccion!.codigo}`}
      onCerrar={onCerrar}
      pie={
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button className="primario" onClick={guardar} disabled={trabajando}>
            {trabajando ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      }
    >
      <Error error={error} />

      <div className="campo">
        <label htmlFor="codigo">Código *</label>
        <input
          id="codigo" className="mono" value={datos.codigo} maxLength={30}
          onChange={(e) => setDatos({ ...datos, codigo: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="numeroParte">Número de parte</label>
        <input
          id="numeroParte" className="mono" value={datos.numeroParte} maxLength={80}
          onChange={(e) => setDatos({ ...datos, numeroParte: e.target.value })}
        />
        <span className="ayuda">
          Distingue dos piezas que comparten código. Hay 23 casos así en los datos cargados.
        </span>
      </div>

      <div className="campo">
        <label htmlFor="nombre">Nombre *</label>
        <input
          id="nombre" value={datos.nombre} maxLength={250}
          onChange={(e) => setDatos({ ...datos, nombre: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="descripcion">Descripción</label>
        <textarea
          id="descripcion" value={datos.descripcion} maxLength={500} rows={2}
          onChange={(e) => setDatos({ ...datos, descripcion: e.target.value })}
        />
        <span className="ayuda">
          Lo que distingue esta pieza de otra parecida: medida, voltaje, para
          qué sirve. El nombre se lee en las listas y tiene que caber.
        </span>
      </div>

      <div className="campo">
        <label htmlFor="areaId">Área</label>
        <select
          id="areaId" value={datos.areaId}
          onChange={(e) => setDatos({ ...datos, areaId: e.target.value })}
        >
          <option value="">— sin asignar —</option>
          {areas.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
        </select>
      </div>

      {esAlta && (
        <>
          <h3>Dónde sirve</h3>
          <p className="ayuda">
            Opcional, y se puede agregar más de una combinación después,
            desde el detalle de la pieza.
          </p>

          <div className="campo">
            <label htmlFor="compatEquipo">Equipo</label>
            <select
              id="compatEquipo" value={compat.tipoEquipoId}
              onChange={(e) => setCompat({
                ...compat, tipoEquipoId: e.target.value, modeloId: '',
              })}
            >
              <option value="">— cualquiera —</option>
              {tiposEquipo.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>
          </div>

          <div className="campo">
            <label htmlFor="compatMarca">Marca</label>
            <select
              id="compatMarca" value={compat.marcaId}
              onChange={(e) => setCompat({
                ...compat, marcaId: e.target.value, modeloId: '',
              })}
            >
              <option value="">— cualquiera —</option>
              {marcas.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
            </select>
          </div>

          <div className="campo">
            <label htmlFor="compatModelo">Modelo</label>
            <select
              id="compatModelo" value={compat.modeloId} disabled={!compat.marcaId}
              onChange={(e) => setCompat({ ...compat, modeloId: e.target.value })}
            >
              <option value="">{compat.marcaId ? '— cualquiera —' : 'Elige una marca'}</option>
              {modelos.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
            </select>
          </div>

          <h3>Bodega</h3>
          <SelectorDeBodega valor={bodegaId} onCambiar={setBodegaId} />
        </>
      )}

      <h3>Despiece</h3>
      <Despiece
        refaccion={refaccion}
        archivo={archivo}
        onElegir={setArchivo}
      />

      <div className="campo">
        <label htmlFor="unidadMedida">Unidad de medida</label>
        <input
          id="unidadMedida" value={datos.unidadMedida} maxLength={20}
          onChange={(e) => setDatos({ ...datos, unidadMedida: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="stockMinimo">Stock mínimo</label>
        <input
          id="stockMinimo" type="number" min={0} value={datos.stockMinimo}
          onChange={(e) => setDatos({ ...datos, stockMinimo: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="costoPromedio">Costo promedio</label>
        <input
          id="costoPromedio" type="number" min={0} step="0.01" value={datos.costoPromedio}
          onChange={(e) => setDatos({ ...datos, costoPromedio: e.target.value })}
        />
        <span className="ayuda">
          Lo recalcula el almacén con cada entrada. Aquí sólo se fija el valor inicial.
        </span>
      </div>

      <div className="campo">
        <label htmlFor="precioVenta">Precio de venta sugerido</label>
        <input
          id="precioVenta" type="number" min={0} step="0.01" value={datos.precioVentaSugerido}
          onChange={(e) => setDatos({ ...datos, precioVentaSugerido: e.target.value })}
        />
      </div>
    </Dialogo>
  );
}

// ══════════════════════════════════════════════════ compatibilidades

function Compatibilidades({
  refaccion, tiposEquipo, marcas, puedeEditar, onCerrar,
}: {
  refaccion: Refaccion;
  tiposEquipo: Opcion[];
  marcas: Opcion[];
  puedeEditar: boolean;
  onCerrar: () => void;
}) {
  const [lista, setLista] = useState<Compatibilidad[] | null>(null);
  // La ficha completa: la fila de la tabla no trae el despiece.
  const [ficha, setFicha] = useState<Refaccion | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [nuevo, setNuevo] = useState({ tipoEquipoId: '', marcaId: '', modeloId: '' });
  const [modelos, setModelos] = useState<Opcion[]>([]);
  const [trabajando, setTrabajando] = useState(false);

  const cargar = useCallback(() => {
    api.get<Refaccion & { compatibilidades: Compatibilidad[] }>(`/refacciones/${refaccion.id}`)
      .then((r) => { setLista(r.compatibilidades); setFicha(r); })
      .catch(setError);
  }, [refaccion.id]);

  useEffect(cargar, [cargar]);

  /**
   * Los modelos, acotados a la marca **y al tipo de equipo**.
   *
   * Faltaba el tipo, y de ahí salía el 500 al guardar: se podía elegir
   * «cámara de refrigeración» con un modelo de la marca que en realidad
   * es de un horno, y el disparador de la 0002 lo rechazaba en SQL. La
   * pantalla decía «Ocurrió un error inesperado».
   *
   * Por marca y no todos: son más de cuatrocientos.
   */
  useEffect(() => {
    if (!nuevo.marcaId) { setModelos([]); return; }
    api.get<Opcion[]>('/catalogos/modelos/opciones', {
      marca_id: nuevo.marcaId,
      tipo_equipo_id: nuevo.tipoEquipoId || undefined,
    })
      .then(setModelos)
      .catch(() => setModelos([]));
  }, [nuevo.marcaId]);

  async function agregar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post(`/refacciones/${refaccion.id}/compatibilidades`, {
        tipoEquipoId: nuevo.tipoEquipoId || undefined,
        marcaId: nuevo.marcaId || undefined,
        modeloId: nuevo.modeloId || undefined,
      });
      setNuevo({ tipoEquipoId: '', marcaId: '', modeloId: '' });
      cargar();
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  async function quitar(id: string) {
    setError(null);
    try {
      await api.borrar(`/refacciones/${refaccion.id}/compatibilidades/${id}`);
      cargar();
    } catch (e) {
      setError(e);
    }
  }

  return (
    <Dialogo
      titulo={`${refaccion.codigo} — ${refaccion.nombre}`}
      onCerrar={onCerrar}
      pie={<button onClick={onCerrar}>Cerrar</button>}
    >
      <Error error={error} />

      {refaccion.descripcion && <p>{refaccion.descripcion}</p>}

      {/* Arriba del todo y no al final: quien abre esta ficha a media
          entrega suele venir por el plano, no por la matriz. */}
      <p>
        <strong>Despiece:</strong>{' '}
        {ficha?.despiece ? (
          <a
            href={`/api/v1/refacciones/${refaccion.id}/despiece`}
            target="_blank"
            rel="noreferrer"
          >
            {ficha.despiece.nombre}
          </a>
        ) : (
          <span className="conteo">sin documento</span>
        )}
      </p>

      <p className="sub">
        Equipos donde sirve esta pieza. Se puede declarar sólo el tipo de
        equipo, o afinar hasta el modelo.
      </p>

      {lista === null ? <Cargando /> : lista.length === 0 ? (
        <div className="vacio">Sin compatibilidades registradas.</div>
      ) : (
        <table>
          <thead>
            <tr><th>Tipo de equipo</th><th>Marca</th><th>Modelo</th>{puedeEditar && <th />}</tr>
          </thead>
          <tbody>
            {lista.map((c) => (
              <tr key={c.id}>
                <td>{c.tipoEquipo?.nombre ?? '—'}</td>
                <td>{c.marca?.nombre ?? '—'}</td>
                <td>{c.modelo?.nombre ?? '—'}</td>
                {puedeEditar && (
                  <td className="acciones">
                    <button className="enlace" onClick={() => quitar(c.id)}>Quitar</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {puedeEditar && (
        <div className="panel" style={{ marginTop: 6 }}>
          <h2 style={{ marginBottom: 10 }}>Agregar</h2>
          <div className="controles">
            <select
              value={nuevo.tipoEquipoId}
              onChange={(e) => setNuevo({
                ...nuevo, tipoEquipoId: e.target.value, modeloId: '',
              })}
            >
              <option value="">— tipo de equipo —</option>
              {tiposEquipo.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>
            <select
              value={nuevo.marcaId}
              onChange={(e) => setNuevo({ ...nuevo, marcaId: e.target.value, modeloId: '' })}
            >
              <option value="">— marca —</option>
              {marcas.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
            </select>
            <select
              value={nuevo.modeloId}
              disabled={!nuevo.marcaId}
              onChange={(e) => setNuevo({ ...nuevo, modeloId: e.target.value })}
            >
              <option value="">— modelo —</option>
              {modelos.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
            </select>
            <button
              className="primario"
              onClick={agregar}
              disabled={trabajando || (!nuevo.tipoEquipoId && !nuevo.marcaId && !nuevo.modeloId)}
            >
              Agregar
            </button>
          </div>
          <span className="ayuda">
            Al elegir modelo, su marca y su tipo de equipo se completan solos.
          </span>
        </div>
      )}
    </Dialogo>
  );
}

function CambioEstadoRefaccion({
  refaccion, onCerrar, onHecho,
}: {
  refaccion: Refaccion;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  async function confirmar() {
    setError(null);
    setTrabajando(true);
    try {
      const accion = refaccion.activo ? 'desactivar' : 'reactivar';
      await api.post(`/refacciones/${refaccion.id}/${accion}`, { motivo });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <DialogoMotivo
      titulo={`${refaccion.activo ? 'Desactivar' : 'Reactivar'} refacción`}
      textoBoton={refaccion.activo ? 'Desactivar' : 'Reactivar'}
      motivo={motivo}
      setMotivo={setMotivo}
      trabajando={trabajando}
      error={error}
      onConfirmar={confirmar}
      onCerrar={onCerrar}
      descripcion={
        <p>
          <strong>{refaccion.codigo} — {refaccion.nombre}</strong>
          {refaccion.activo
            ? ' dejará de ofrecerse al capturar solicitudes y compras.'
            : ' volverá a estar disponible.'}
        </p>
      }
    />
  );
}

// ══════════════════════════════════════════════════════ carga masiva

interface ResultadoCarga {
  simulacion: boolean;
  archivo: string;
  filaEncabezado: number;
  columnasIgnoradas: string[];
  renglones: number;
  piezas: number;
  creadas: number;
  actualizadas: number;
  compatibilidadesNuevas: number;
  existenciasAjustadas: number;
  rechazados: { numero: number; codigo: string | null; motivo: string }[];
  avisos: { codigo: string; renglon: number; texto: string }[];
}

/**
 * Actualizar el catálogo desde un CSV.
 *
 * Primero se simula y después se aplica, siempre. Con 650 renglones,
 * enterarse de que la columna de marcas no casaba con el catálogo es muy
 * distinto antes o después de haber escrito en la base: antes se corrige
 * el archivo, después hay que deshacer a mano.
 */
function CargaDeRefacciones({
  onCerrar, onAplicada,
}: {
  onCerrar: () => void;
  onAplicada: () => void;
}) {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [resultado, setResultado] = useState<ResultadoCarga | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  async function enviar(simular: boolean) {
    if (!archivo) return;
    setError(null);
    setTrabajando(true);
    try {
      const cuerpo = new FormData();
      cuerpo.append('archivo', archivo);

      const respuesta = await fetchAutenticado(
        `/api/v1/refacciones/carga?simular=${simular ? 'si' : 'no'}`,
        { method: 'POST', body: cuerpo },
      );

      const texto = await respuesta.text();
      const cuerpoRespuesta = texto ? JSON.parse(texto) : {};

      if (!respuesta.ok) {
        throw new ErrorApi(
          respuesta.status,
          String(cuerpoRespuesta.codigo_negocio ?? 'error'),
          String(cuerpoRespuesta.detail ?? 'No se pudo leer el archivo.'),
        );
      }

      setResultado(cuerpoRespuesta as ResultadoCarga);
      if (!simular) onAplicada();
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  const simulado = resultado?.simulacion === true;

  return (
    <Dialogo
      titulo="Cargar refacciones desde CSV"
      ancho
      onCerrar={onCerrar}
      pie={
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cerrar</button>
          <button onClick={() => void enviar(true)} disabled={!archivo || trabajando}>
            {trabajando ? 'Leyendo…' : 'Probar sin guardar'}
          </button>
          <button
            className="primario"
            onClick={() => void enviar(false)}
            disabled={!archivo || trabajando || !simulado}
            title={simulado ? undefined : 'Primero pruébalo sin guardar'}
          >
            Aplicar
          </button>
        </>
      }
    >
      <Error error={error} />

      <div className="campo">
        <label htmlFor="archivoRefacciones">Archivo CSV</label>
        <input
          id="archivoRefacciones" type="file" accept=".csv,text/csv"
          onChange={(e) => { setArchivo(e.target.files?.[0] ?? null); setResultado(null); }}
        />
        <span className="ayuda">
          Basta la columna de código. Lo demás es opcional: nombre, número de parte,
          descripción, unidad, mínimo, precio, área, equipo, marca, modelo, bodega
          y existencia. Una celda vacía deja el dato como está.
        </span>
      </div>

      <div className="aviso">
        Un código que ya existe se actualiza; uno nuevo se da de alta. Las
        compatibilidades se suman y no se borran. Si traes bodega y existencia,
        el inventario queda como diga el archivo.
      </div>

      {resultado && (
        <>
          <h2>{simulado ? 'Así quedaría' : 'Aplicado'}</h2>

          <div className="rejilla-datos">
            <Dato etiqueta="Renglones leídos" valor={String(resultado.renglones)} />
            <Dato etiqueta="Piezas distintas" valor={String(resultado.piezas)} />
            <Dato etiqueta="Nuevas" valor={String(resultado.creadas)} />
            <Dato etiqueta="Actualizadas" valor={String(resultado.actualizadas)} />
            <Dato
              etiqueta="Compatibilidades nuevas"
              valor={String(resultado.compatibilidadesNuevas)}
            />
            <Dato
              etiqueta="Existencias fijadas"
              valor={String(resultado.existenciasAjustadas)}
            />
          </div>

          {resultado.columnasIgnoradas.length > 0 && (
            <p className="ayuda">
              Columnas que no se reconocieron: {resultado.columnasIgnoradas.join(', ')}.
              Si alguna debía cargarse, revisa cómo está escrita.
            </p>
          )}

          {resultado.rechazados.length > 0 && (
            <>
              <h2>No se cargaron ({resultado.rechazados.length})</h2>
              <table>
                <thead>
                  <tr><th>Renglón</th><th>Código</th><th>Motivo</th></tr>
                </thead>
                <tbody>
                  {resultado.rechazados.map((r) => (
                    <tr key={`${r.numero}-${r.codigo}`}>
                      <td className="numero">{r.numero}</td>
                      <td className="mono">{r.codigo ?? '—'}</td>
                      <td>{r.motivo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {resultado.avisos.length > 0 && (
            <>
              <h2>Avisos ({resultado.avisos.length})</h2>
              <p className="sub">Se cargaron, pero algo del renglón no se pudo usar.</p>
              <table>
                <thead>
                  <tr><th>Renglón</th><th>Código</th><th>Qué pasó</th></tr>
                </thead>
                <tbody>
                  {resultado.avisos.slice(0, 100).map((a, i) => (
                    <tr key={`${a.codigo}-${i}`}>
                      <td className="numero">{a.renglon}</td>
                      <td className="mono">{a.codigo}</td>
                      <td>{a.texto}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {resultado.avisos.length > 100 && (
                <p className="ayuda">Se muestran los primeros 100.</p>
              )}
            </>
          )}
        </>
      )}
    </Dialogo>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="campo">
      <label>{etiqueta}</label>
      <span>{valor}</span>
    </div>
  );
}
